import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, TextField } from '@mui/material';
import { debounce } from '@mui/material/utils';


export type Option = {
    id: number,
    label: string,
    type: string,
}

type Props = {
    search: (s: string) => Promise<any>,
    valueRef?: React.MutableRefObject<Option | null>,
    onChange?: (r: Option | null) => void,
    label?: string,
    margin?: 'dense' | 'normal' | 'none',
    keepValue?: boolean,
}

const AutoSearch = ({onChange, search, valueRef, label, margin, keepValue}: Props) => {
    const {t} = useTranslation();
    const [value, setValue] = useState<Option | null>(null);
    const [inputValue, setInputValue] = useState('');
    const [options, setOptions] = useState<Option[]>([]);

    const get = useMemo(
        () => debounce((s: string, callback: (results: Option[]) => void) => {
            search(s).then(callback)
        }, 400),
        [search]
    );

    useEffect(() => {
        let active = true;

        if (inputValue === '') {
            setOptions([]);
            return;
        }

        get(inputValue, results => {
            if (active) setOptions(results);
        });

        return () => {
            active = false;
        };
    }, [value, inputValue, get]);

    return (
        <Autocomplete
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={option => option.label}
            filterOptions={x => x}
            options={options}
            autoComplete
            fullWidth
            includeInputInList
            filterSelectedOptions
            value={value}
            noOptionsText={t('no_options')}
            onChange={(e, value) => {
                if (onChange)
                    onChange(value);
                else if (valueRef)
                    valueRef.current = value;

                if (keepValue) setValue(value);
            }}
            onInputChange={(e, value) => setInputValue(value)}
            renderInput={(params: any) => (
                <TextField
                    {...params}
                    fullWidth
                    label={label}
                    margin={margin}
                    variant="outlined"
                />
            )}
            renderOption={(props, option) => (
                <li {...props} key={option.id}>
                    {option.label}
                </li>
            )}
        />
    );
}

export default AutoSearch;
